import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/authContext';
import { onNodesChange, addNode, deleteNode, uploadFile, updateNodeDetails } from '../../firebase/files';
import { Folder, File, Upload, Plus, X, Trash2, ChevronRight, Home, Pencil } from 'lucide-react';

const AddFolderModal = ({ isOpen, onClose, onSubmit, isSubmitting }) => {
    const [folderName, setFolderName] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (folderName.trim()) {
            onSubmit(folderName);
        }
    };
    
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-md relative">
                <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                    <X className="h-5 w-5" />
                </button>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Create New Folder</h2>
                    <div>
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Folder Name</label>
                        <input
                            type="text"
                            value={folderName}
                            onChange={(e) => setFolderName(e.target.value)}
                            required
                            placeholder="My New Folder"
                            className="w-full mt-2 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600"
                        />
                    </div>
                    <button type="submit" disabled={isSubmitting} className={`w-full px-4 py-2 text-white font-medium rounded-lg ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}>
                        {isSubmitting ? 'Creating...' : 'Create Folder'}
                    </button>
                </form>
            </div>
        </div>
    );
};

const InfoModal = ({ isOpen, onClose, title, message }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-sm relative">
                 <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                    <X className="h-5 w-5" />
                </button>
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">{title}</h2>
                <p className="mt-4 text-gray-600 dark:text-gray-400">{message}</p>
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors">
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
};

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, node, isDeleting }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-sm">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Confirm Deletion</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Are you sure you want to delete "{node?.name}"? This action cannot be undone.
                </p>
                <div className="mt-6 flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        disabled={isDeleting}
                        className="px-4 py-2 rounded-lg font-medium bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isDeleting}
                        className="px-4 py-2 rounded-lg font-medium bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const FileBrowser = ({ projectId, projectOwnerId }) => {
    const { currentUser } = useAuth();
    const [nodes, setNodes] = useState([]);
    const [path, setPath] = useState([{ id: null, name: 'Home' }]);
    const [loading, setLoading] = useState(true);
    const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const fileInputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const dragCounter = useRef(0);
    const [infoModalState, setInfoModalState] = useState({ isOpen: false, title: '', message: '' });

    const [isDeleting, setIsDeleting] = useState(false);
    const [nodeToDelete, setNodeToDelete] = useState(null);

    const [draggedNodeId, setDraggedNodeId] = useState(null);
    const [dropTargetId, setDropTargetId] = useState(null);

    const [editingNodeId, setEditingNodeId] = useState(null);
    const [tempNodeName, setTempNodeName] = useState('');

    const currentFolder = path[path.length - 1];

    useEffect(() => {
        setLoading(true);
        const unsubscribe = onNodesChange(projectId, currentFolder.id, (newNodes) => {
            setNodes(newNodes);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [projectId, currentFolder.id]);
    
    const handleFolderClick = (folder) => {
        setPath([...path, folder]);
    };

    const handleBreadcrumbClick = (index) => {
        setPath(path.slice(0, index + 1));
    };

    const handleCreateFolder = async (folderName) => {
        setIsSubmitting(true);
        const newFolder = {
            name: folderName,
            type: 'folder',
            parentId: currentFolder.id,
        };
        try {
            await addNode(projectId, newFolder, currentUser.uid);
            setIsFolderModalOpen(false);
        } catch (error) {
            console.error("Error creating folder:", error);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const canManage = (currentUser.uid === projectOwnerId && currentUser.role !== 'customer') || ['superadmin', 'admin', 'collaborator'].includes(currentUser.role);
    
    const handleFilesSelected = (files) => {
        if (files && files.length > 0) {
            handleUpload(Array.from(files));
        }
    }
    
    const handleUpload = async (files) => {
        if (!canManage) return;

        const currentUploads = {};
        files.forEach(file => {
            currentUploads[file.name] = 0;
        });
        setUploadProgress(prev => ({...prev, ...currentUploads}));

        const uploadPromises = files.map(file => 
            uploadFile(projectId, currentFolder.id, file, currentUser.uid, (progress) => {
                setUploadProgress(prev => ({...prev, [file.name]: progress }));
            })
        );
        
        try {
            await Promise.all(uploadPromises);
        } catch (error) {
            console.error("One or more uploads failed", error);
            let title = "Upload Failed";
            let message = "An unknown error occurred during upload. Please try again.";

            if (error && error.code) {
                switch(error.code) {
                    case 'storage/unauthorized':
                        message = `Permission denied. Please ensure you have the correct permissions to upload files. The storage rules for Firebase may need to be updated to allow writes.`;
                        break;
                    case 'storage/canceled':
                        message = 'Upload was canceled.';
                        break;
                    case 'storage/unknown':
                        if (error.message.includes('CORS')) {
                             title = "Cross-Origin (CORS) Error";
                             message = "The browser blocked the upload due to a security policy. This is a common issue during development that requires a configuration change on the backend (Firebase Storage) to allow requests from this website's domain. This is not an error in the application code.";
                        }
                        break;
                    default:
                        message = `An unexpected error occurred: ${error.message}`;
                }
            }
            setInfoModalState({ isOpen: true, title, message });
        }
    };
    
    const handleDragEvents = (e) => {
        e.preventDefault();
        e.stopPropagation();
    }
    
    const handleDragEnter = (e) => {
        handleDragEvents(e);
        dragCounter.current++;
        if (canManage && e.dataTransfer.types.includes('Files')) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e) => {
        handleDragEvents(e);
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    };

    const handleDrop = (e) => {
        handleDragEvents(e);
        dragCounter.current = 0;
        setIsDragging(false);

        if (e.dataTransfer.types.includes('application/node-id')) {
            const nodeId = e.dataTransfer.getData('application/node-id');
            if (nodeId && nodeId !== currentFolder.id) {
                handleNodeMove(nodeId, currentFolder.id);
            }
        } else if (e.dataTransfer.files && e.dataTransfer.files.length > 0 && canManage) {
            handleFilesSelected(e.dataTransfer.files);
        }
        setDraggedNodeId(null);
    };

    const handleNodeDragStart = (e, node) => {
        e.stopPropagation();
        if (!canManage) return;
        setDraggedNodeId(node.id);
        e.dataTransfer.setData('application/node-id', node.id);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleNodeDragEnd = () => {
        setDraggedNodeId(null);
        setDropTargetId(null);
    };

    const handleFolderDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (draggedNodeId) {
            e.dataTransfer.dropEffect = 'move';
        }
    };

    const handleFolderDragEnter = (e, folderId) => {
        e.preventDefault();
        e.stopPropagation();
        if (draggedNodeId && draggedNodeId !== folderId) {
            setDropTargetId(folderId);
        }
    };

    const handleFolderDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDropTargetId(null);
    };

    const handleFolderDrop = (e, targetFolderNode) => {
        e.preventDefault();
        e.stopPropagation();
        setDropTargetId(null);
        const nodeId = e.dataTransfer.getData('application/node-id');
        if (nodeId && nodeId !== targetFolderNode.id) {
            handleNodeMove(nodeId, targetFolderNode.id);
        }
    };
    
    const handleBreadcrumbDrop = (e, folderId) => {
        e.preventDefault();
        e.stopPropagation();
        setDropTargetId(null);
        const nodeId = e.dataTransfer.getData('application/node-id');
        if (nodeId) {
            handleNodeMove(nodeId, folderId);
        }
    };

    const handleNodeMove = async (nodeId, newParentId) => {
        if (!canManage) return;
        try {
            await updateNodeDetails(projectId, nodeId, { parentId: newParentId });
        } catch (error) {
            console.error("Failed to move node:", error);
            setInfoModalState({ isOpen: true, title: 'Move Failed', message: `An error occurred while moving the item.` });
        }
    };


    useEffect(() => {
        const completed = Object.entries(uploadProgress).filter(([,p]) => p >= 100);
        if (completed.length > 0) {
            setTimeout(() => {
                setUploadProgress(prev => {
                    const next = {...prev};
                    completed.forEach(([name]) => delete next[name]);
                    return next;
                })
            }, 5000);
        }
    }, [uploadProgress]);

    const handleDeleteRequest = (node) => {
        setNodeToDelete(node);
    };
    
    const handleConfirmDelete = async () => {
        if (!nodeToDelete) return;
        setIsDeleting(true);
        try {
            await deleteNode(projectId, nodeToDelete.id);
        } catch (error) {
            console.error("Failed to delete node:", error);
            setInfoModalState({ isOpen: true, title: 'Deletion Failed', message: `Failed to delete ${nodeToDelete.name}. You may not have permissions.` });
        } finally {
            setIsDeleting(false);
            setNodeToDelete(null);
        }
    };

    const handleRenameRequest = (node) => {
        if (!canManage) return;
        setEditingNodeId(node.id);
        setTempNodeName(node.name);
    };
    
    const handleConfirmRename = async () => {
        if (!editingNodeId || !tempNodeName.trim()) {
            setEditingNodeId(null);
            return;
        }
        if (!canManage) return;
    
        try {
            const originalNode = nodes.find(n => n.id === editingNodeId);
            if (originalNode && originalNode.name === tempNodeName.trim()) {
                setEditingNodeId(null);
                return;
            }
            await updateNodeDetails(projectId, editingNodeId, { name: tempNodeName.trim() });
        } catch (error) {
            console.error("Error renaming node:", error);
            setInfoModalState({ isOpen: true, title: 'Rename Failed', message: `An error occurred while renaming.` });
        } finally {
            setEditingNodeId(null);
            setTempNodeName('');
        }
    };
    
    const handleRenameKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleConfirmRename();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setEditingNodeId(null);
            setTempNodeName('');
        }
    };

    return (
        <div className="space-y-4">
            <input type="file" ref={fileInputRef} onChange={(e) => handleFilesSelected(e.target.files)} multiple className="hidden" />
             <div className="flex justify-between items-center">
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    {path.map((folder, index) => (
                        <React.Fragment key={folder.id || 'home'}>
                            <button 
                                onClick={() => handleBreadcrumbClick(index)}
                                onDragOver={handleFolderDragOver}
                                onDragEnter={(e) => handleFolderDragEnter(e, folder.id)}
                                onDragLeave={handleFolderDragLeave}
                                onDrop={(e) => handleBreadcrumbDrop(e, folder.id)}
                                className={`p-1 rounded-md ${dropTargetId === folder.id ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600' : 'hover:underline'}`}
                            >
                                {index === 0 ? <Home className="h-4 w-4 inline-block mr-1" /> : ''}
                                {folder.name}
                            </button>
                            {index < path.length - 1 && <ChevronRight className="h-4 w-4 mx-1" />}
                        </React.Fragment>
                    ))}
                </div>
                {canManage && (
                    <div className="flex gap-2">
                        <button onClick={() => fileInputRef.current.click()} className="flex items-center gap-2 p-2 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                            <Upload className="h-4 w-4" />
                            <span className="hidden sm:inline">Upload</span>
                        </button>
                        <button onClick={() => setIsFolderModalOpen(true)} className="flex items-center gap-2 p-2 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                            <Plus className="h-4 w-4" />
                            <span className="hidden sm:inline">New Folder</span>
                        </button>
                    </div>
                )}
            </div>

            <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragEvents}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-lg p-4 min-h-[10rem] transition-colors ${
                    isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/10' : 'border-gray-300 dark:border-gray-600'
                }`}
            >
                {isDragging && canManage && (
                     <div className="absolute inset-0 flex flex-col justify-center items-center pointer-events-none z-10">
                        <Upload className="h-8 w-8 text-blue-500 animate-bounce" />
                        <p className="mt-2 font-semibold text-blue-600 dark:text-blue-400">Drop files to upload</p>
                    </div>
                )}

                {loading ? (
                    <p className="text-gray-500 dark:text-gray-400 p-4">Loading...</p>
                ) : nodes.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-6 gap-y-8">
                        {nodes.map(node => (
                           <div 
                            key={node.id} 
                            className={`group relative transition-colors ${draggedNodeId === node.id ? 'opacity-30' : 'opacity-100'}`}
                            draggable={canManage}
                            onDragStart={(e) => handleNodeDragStart(e, node)}
                            onDragEnd={handleNodeDragEnd}
                           >
                                {node.type === 'folder' ? (
                                    <button 
                                        onClick={() => handleFolderClick(node)} 
                                        className="w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 rounded-lg text-center"
                                        onDragOver={handleFolderDragOver}
                                        onDragEnter={(e) => handleFolderDragEnter(e, node.id)}
                                        onDragLeave={handleFolderDragLeave}
                                        onDrop={(e) => handleFolderDrop(e, node)}
                                    >
                                        <div className={`aspect-square bg-gray-100 dark:bg-gray-700/50 rounded-lg flex items-center justify-center border-2 group-hover:border-blue-500 ${dropTargetId === node.id ? 'border-blue-500' : 'border-transparent'}`}>
                                            <Folder className="h-1/2 w-1/2 text-blue-500" />
                                        </div>
                                        {editingNodeId === node.id ? (
                                            <input
                                                type="text"
                                                value={tempNodeName}
                                                onChange={(e) => setTempNodeName(e.target.value)}
                                                onBlur={handleConfirmRename}
                                                onKeyDown={handleRenameKeyDown}
                                                onClick={(e) => e.stopPropagation()}
                                                autoFocus
                                                className="w-full mt-2 text-center text-sm font-medium bg-transparent border border-blue-500 rounded-md text-gray-800 dark:text-gray-200 p-1"
                                            />
                                        ) : (
                                            <p className="mt-2 text-sm text-center font-medium text-gray-800 dark:text-gray-200 truncate">{node.name}</p>
                                        )}
                                    </button>
                                ) : (
                                    <a href={node.url} target="_blank" rel="noopener noreferrer" className="w-full group focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 rounded-lg text-center">
                                        <div className="aspect-square bg-gray-200 dark:bg-gray-700/50 rounded-lg overflow-hidden border-2 border-transparent group-hover:border-blue-500">
                                             {node.fileType?.startsWith('image/') ? (
                                                <img src={node.url} alt={node.name} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <File className="h-1/2 w-1/2 text-gray-500" />
                                                </div>
                                            )}
                                        </div>
                                        {editingNodeId === node.id ? (
                                            <input
                                                type="text"
                                                value={tempNodeName}
                                                onChange={(e) => setTempNodeName(e.target.value)}
                                                onBlur={handleConfirmRename}
                                                onKeyDown={handleRenameKeyDown}
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                autoFocus
                                                className="w-full mt-2 text-center text-sm font-medium bg-transparent border border-blue-500 rounded-md text-gray-800 dark:text-gray-200 p-1"
                                            />
                                        ) : (
                                            <p className="mt-2 text-sm text-center font-medium text-gray-800 dark:text-gray-200 truncate">{node.name}</p>
                                        )}
                                    </a>
                                )}

                                {canManage && (
                                    <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                        <button
                                            onClick={() => handleRenameRequest(node)}
                                            className="p-1.5 bg-white/50 dark:bg-black/50 backdrop-blur-sm rounded-full text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                                            aria-label={`Rename ${node.name}`}
                                        >
                                            <Pencil className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => handleDeleteRequest(node)} className="p-1.5 bg-white/50 dark:bg-black/50 backdrop-blur-sm rounded-full text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400" aria-label={`Delete ${node.name}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                        <p>This folder is empty.</p>
                        {canManage && <p className="text-sm mt-2">Drag and drop files here to upload.</p>}
                    </div>
                )}
            </div>
            
            {Object.keys(uploadProgress).length > 0 && (
                 <div className="space-y-3 pt-4">
                    <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300">Uploading...</h3>
                    {Object.entries(uploadProgress).map(([name, progress]) => (
                        <div key={name} className="flex items-center gap-3">
                            <File className="h-5 w-5 text-gray-500 flex-shrink-0" />
                            <div className="flex-grow">
                                <p className="text-sm text-gray-800 dark:text-gray-200 truncate">{name}</p>
                                <div className="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                    <div className="bg-blue-600 h-2 rounded-full transition-all duration-150" style={{ width: `${progress}%` }}></div>
                                </div>
                            </div>
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-12 text-right">{Math.round(progress)}%</span>
                        </div>
                    ))}
                </div>
            )}
            
            <AddFolderModal 
                isOpen={isFolderModalOpen}
                onClose={() => setIsFolderModalOpen(false)}
                onSubmit={handleCreateFolder}
                isSubmitting={isSubmitting}
            />
            <InfoModal 
                isOpen={infoModalState.isOpen}
                onClose={() => setInfoModalState({ isOpen: false, title: '', message: '' })}
                title={infoModalState.title}
                message={infoModalState.message}
            />
             <DeleteConfirmationModal 
                isOpen={!!nodeToDelete}
                onClose={() => setNodeToDelete(null)}
                onConfirm={handleConfirmDelete}
                node={nodeToDelete}
                isDeleting={isDeleting}
            />
        </div>
    );
};

export default FileBrowser;
